// ---------------------------------------------------------------------------
// WorkspaceDrawer — overlay shell for workspace management.
//
// Mounted once at the app root. Reads open state from WorkspaceDrawerContext
// and renders the matching view (list / create / settings). Replaces the
// previous page-based routing so workspace operations don't take the user
// away from their current working context.
// ---------------------------------------------------------------------------

import {
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ArrowLeft20Filled,
  ArrowLeft20Regular,
  Dismiss20Filled,
  Dismiss20Regular,
} from '@fluentui/react-icons';

import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import { TelemetryPanel } from '../../telemetry/components/TelemetryPanel/TelemetryPanel.js';
import { PanelRegion, PanelType } from '../../telemetry/userbi.js';

import { CreateView } from './CreateView.js';
import { ListView } from './ListView.js';
import { SettingsView } from './SettingsView.js';
import { useStyles } from './WorkspaceDrawer.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const ArrowLeftIcon = bundleIcon(ArrowLeft20Filled, ArrowLeft20Regular);
const DismissIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

export function WorkspaceDrawer() {
  const styles = useStyles();
  const { view, open, close } = useWorkspaceDrawer();

  const isOpen = view !== null;
  const kind = view?.kind;

  const title =
    kind === 'create'
      ? 'Create workspace'
      : kind === 'settings'
        ? 'Workspace settings'
        : 'Workspaces';

  const subtitle =
    kind === 'settings' && view?.kind === 'settings' ? view.name : undefined;

  const showBack = kind === 'create' || kind === 'settings';

  return (
    <Drawer
      type="overlay"
      position="end"
      open={isOpen}
      onOpenChange={(_e, data) => {
        if (!data.open) close();
      }}
      className={styles.drawer}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<DismissIcon />}
              onClick={close}
            />
          }
        >
          <div className={styles.drawerHeaderRow}>
            {showBack && (
              <Button
                appearance="subtle"
                aria-label="Back to workspace list"
                icon={<ArrowLeftIcon />}
                onClick={() => open({ kind: 'list' })}
              />
            )}
            <span>{title}</span>
          </div>
        </DrawerHeaderTitle>
        {subtitle && <div className={styles.headerSub}>{subtitle}</div>}
      </DrawerHeader>

      <DrawerBody>
        {view !== null && (
          <TelemetryPanel
            key={view.kind}
            panelType={PanelType.WorkspaceDrawer}
            panelRegion={PanelRegion.Overlay}
            eventID={view.kind}
          >
            {view.kind === 'list' && <ListView />}
            {view.kind === 'create' && <CreateView />}
            {view.kind === 'settings' && <SettingsView name={view.name} />}
          </TelemetryPanel>
        )}
      </DrawerBody>
    </Drawer>
  );
}
